import React from "react";
import {
  MessageSquare,
  Camera,
  TrendingUp,
  Cpu,
  ArrowRight,
  Monitor,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const AIMachineLearing = () => {
  const sectionMotionProps = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.6, ease: "easeOut" },
  };

  const mainServices = [
    {
      icon: <MessageSquare className="w-12 h-12" />,
      title: "Natural Language Processing",
      description:
        "Analyze and process human language for chatbots, sentiment analysis, and more.",
    },
    {
      icon: <Monitor className="w-12 h-12" />,
      title: "Computer Vision Solutions",
      description:
        "Detect, recognize, and process images and videos for intelligent automation.",
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Predictive Analytics",
      description: "Forecast trends and make data-driven business decisions.",
    },
    {
      icon: <Cpu className="w-12 h-12" />,
      title: "AI Chatbots & Virtual Assistants",
      description: "Automate customer interactions with smart AI agents.",
    },
  ];

  const additionalServices = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "AI Model Training",
      description:
        "Custom AI models trained on your data for accurate predictions.",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "ML Pipeline Development",
      description:
        "End-to-end machine learning pipelines for automated workflows.",
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Computer Vision Solutions",
      description: "Implementing AI-based image & video recognition systems.",
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "NLP Solutions",
      description:
        "Natural Language Processing solutions for text & voice data.",
    },
  ];

  return (
    <motion.div
      className="min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}>
      {/* Hero Section */}
      <motion.section
        {...sectionMotionProps}
        className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            AI &{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-700">
              Machine Learning
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Intelligent solutions powered by AI & Machine Learning to automate
            processes and optimize business outcomes.
          </p>
        </div>
      </motion.section>

      {/* Main Services*/}
      <motion.section {...sectionMotionProps} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <Card
                key={index}
                className="p-8 hover:shadow-2xl transition-all duration-300 border-0 bg-gray-50 group"
              >
                <div className="text-red-600 mb-6 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Additional Services */}
      <motion.section {...sectionMotionProps} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Additional AI Services
            </h2>
            <p className="text-xl text-gray-600">
              More AI & ML services to accelerate your business intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <Card
                key={index}
                className="p-6 bg-white hover:shadow-xl transition-all duration-300 border-0 text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 text-red-600 rounded-full mb-4 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        {...sectionMotionProps}
        className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Leverage AI?</h2>
          <p className="text-xl mb-8 text-gray-300">
            Let’s build intelligent systems that drive better business outcomes.
          </p>
          <Link to="/contact">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300">
              Start Your AI Project
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </motion.section>
    </motion.div>
  );
};

export default AIMachineLearing;
